import {
  Box,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation, Trans } from "react-i18next";
import profilebg from "../assets/profile.jpg";
import img1 from "../assets/profile/1.jpg";
import img2 from "../assets/profile/2.jpg";
import img3 from "../assets/profile/3.jpg";
import img4 from "../assets/profile/4.jpg";
import img5 from "../assets/profile/5.jpg";
import img6 from "../assets/profile/6.jpg";
import img7 from "../assets/profile/7.jpg";
import img8 from "../assets/profile/8.jpg";
import img9 from "../assets/profile/9.jpg";
import img10 from "../assets/profile/10.jpg";
import FilmStripScroller from "./FilmStripScroller";
import LineBreak from "./LineBreak";

const awards = [
  "2025 World Speed Shooting Championship – 3rd Place (Open Division)",
  "2024 U.S. IPSC Handgun Nationals - 14th Place (Open Division)",
  "2023 IPSC Extreme Pan American - 8th Place (Open Division)",
  "2023 World Speed Shooting Championship – 3rd Place (Carry Optics Division)",
  "2022 World Speed Shooting Championship - 2nd Place (Carry Optics Division)",
  "2021 World Speed Shooting Championship - Champion (Carry Optics Division)",
  "2020, 2022, 2024: World Speed Shooting Championship - 2nd Place (Open Division)",
  "2022 IPSC Level 4 Pan American Handgun Championship - 14th Place (Open Division)",
  "2020 USPSA Area 1 Championship - 2nd Place (Open Division)",
  "2019 USPSA Magnus Cup - High International (Open Division)",
  "2018 USPSA Limited Nationals - High Foreign",
  "2017 USPSA Limited Nationals - High Foreign",
  "2015 World Speed Shooting Championship - Champion (Single Stack Division)",
  "2014 U.S Steel Nationals/3rd Place Production",
  "2013 World Speed Shooting Championships/2nd Production",
  "2013 U.S Steel Nationals/3rd Place Overall",
  "2013 U.S Steel Nationals/3rd Place Limited",
  "2012 Phoenix Handgunner Shoot-Off/Open Champion",
  "2012 U.S Steel Nationals/8th Overall",
  "2011 World Speed Shooting Championships/4th Iron Sight Pistol",
  "2007 Japan Steel Challenge/3rd place (For the first time)",
];

const images = [img6, img2, img3, img4, img5, img1, img7, img8, img9, img10];

export default function ProfileSection() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${profilebg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 30%",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="profile"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          {/* Name / Title */}
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary">
              {t("profile.name")}
            </Typography>

            <Typography variant="subtitle1" color="text.primary">
              <Trans
                i18nKey="profile.caption"
                components={{
                  br: <LineBreak />,
                }}
              />
            </Typography>
          </Box>

          {/* Description */}
          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="profile.intro"
                components={{
                  br: <LineBreak />,
                }}
              />
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                mt: 1,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {t("profile.trainingTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("profile.trainingContent")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                mt: 1,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {t("profile.mediaTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("profile.mediaContent")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                mt: 1,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {t("profile.operationsTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="profile.operationsContent"
                components={{
                  br: <LineBreak />,
                }}
              />
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                mt: 1,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {t("profile.techTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("profile.techContent")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                mt: 1,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              {t("profile.pursuitTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="profile.pursuitContent"
                components={{
                  br: <LineBreak />,
                }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ my: 2 }}
            >
              <EmojiEventsIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("profile.awardsTitle")}
            </Typography>
            <FilmStripScroller images={images} height={250} speed={20} />
            <List sx={{ mt: 2 }}>
              {awards.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
